/**
 * Тулбар отбора раздела «Планы»: чип отбора с карточки товара, списки,
 * даты, окно уходимости, поиск, три переключателя, сброс. Вынесено из
 * `pages/PlansPage.tsx` (нарезка 14.10.26).
 */

import type { SetURLSearchParams } from "react-router";
import { Chip } from "@/components/Chip";
import { Combo } from "@/components/Combo";
import { DateRangePicker, созданныеОкна } from "@/components/DateRangePicker";
import { Segmented } from "@/components/Segmented";
import { WindowPicker } from "@/components/WindowPicker";
import { nf } from "@/lib/format";
import { plural } from "@/lib/plans";
import { ТРИ, ТРИ_ОПЛАТА, ВСЕ } from "./filters";
import type { FiltersPayload } from "./types";

export function PlansToolbar({
  params,
  setParams,
  set,
  setRange,
  setCreatedRange,
  searchDraft,
  setSearchDraft,
  scopeLabel,
  clearScope,
  idsCount,
  clearIds,
  statuses,
  produceStatuses,
  suppliers,
  набор,
  categories,
  windowDays,
}: {
  params: URLSearchParams;
  setParams: SetURLSearchParams;
  /** Одна правка адреса по образцу (`patchParams`). */
  set: (key: string, value: string) => void;
  setRange: (from: string, to: string) => void;
  setCreatedRange: (from: string, to: string) => void;
  searchDraft: string;
  setSearchDraft: (next: string) => void;
  scopeLabel: string | null;
  clearScope: () => void;
  /** Сколько партий названо в адресе (`ids=`); 0 — отбора нет. */
  idsCount: number;
  clearIds: () => void;
  statuses: FiltersPayload["statuses"];
  produceStatuses: FiltersPayload["produce_statuses"];
  suppliers: FiltersPayload["suppliers"];
  /** Набор поставщиков из адреса (`supplier=1,2,3`). */
  набор: string[];
  categories: FiltersPayload["categories"];
  windowDays: number;
}) {
  return (
    <section className="flex flex-none flex-wrap items-center gap-2 rounded-t-[var(--radius-card)] border border-b-0 border-border bg-bg-subtle p-3">
      {/* Отбор, пришедший кликом из карточки товара. Экран, молча
          показывающий три партии вместо сорока, неотличим от сломанного —
          поэтому чип обязателен, а не украшение. */}
      {scopeLabel && (
        <div data-annot="отбор с карточки товара">
          <Chip
            label={scopeLabel}
            on
            title="отбор пришёл из карточки товара — снимите, чтобы увидеть все партии"
            onClick={clearScope}
          />
        </div>
      )}

      {/* Партии, открытые из «открыть ▾» полосы выбора: без чипа экран с
          тремя партиями неотличим от сломанного. */}
      {idsCount > 0 && (
        <div data-annot="отбор отмеченных">
          <Chip
            label={`отмеченные: ${idsCount} ×`}
            on
            title="отбор по отмеченным партиям — снимите, чтобы увидеть все"
            onClick={clearIds}
          />
        </div>
      )}

      {/* Списки — `Combo`, а не `select` (замечание владельца 15.09.26):
          нативный попап браузер рисует системным — белым в тёмной теме, — и
          поиска в нём нет. Короткие списки идут тем же компонентом: второй
          вид выпадающего списка на одном тулбаре читался бы чужим. */}
      <Combo
        label="статус"
        width={180}
        value={params.get("status") ?? ""}
        // Снять отбор — это записать `all`, а не стереть параметр: стёртый
        // вернул бы дефолт «Активный» тем же эффектом, что ставит его при
        // входе, и первая строка списка не работала бы вовсе.
        onChange={(v) => set("status", v || "all")}
        options={[
          { value: "all", label: "все" },
          ...statuses.map((s) => ({ value: String(s.id), label: s.name })),
        ]}
      />

      <Combo
        label="производство"
        width={220}
        value={params.get("produce") ?? ""}
        onChange={(v) => set("produce", v)}
        options={[
          ВСЕ,
          ...produceStatuses.map((s) => ({
            value: String(s.id),
            label: s.name,
          })),
        ]}
      />

      <Combo
        label="поставщик"
        width={220}
        value={params.get("supplier") ?? ""}
        onChange={(v) => set("supplier", v)}
        // Имя — ключ поиска, иначе короткое (правило кабинета). Счётчик —
        // правой колонкой: у поставщика с двумя партиями и у поставщика с
        // пятьюстами разная цена ошибки отбора.
        options={[
          ВСЕ,
          // Набор с дашборда (`supplier=1,2,3`) — своей строкой, иначе поле
          // показало бы пусто при суженном списке.
          ...(набор.length > 1
            ? [
                {
                  value: params.get("supplier") ?? "",
                  label: `${набор.length} ${plural(набор.length, ["поставщик", "поставщика", "поставщиков"])}`,
                  title: набор
                    .map((id) => suppliers.find((s) => String(s.id) === id)?.code || id)
                    .join(", "),
                },
              ]
            : []),
          ...suppliers.map((s) => ({
            value: String(s.id),
            label: s.code || s.name,
            meta: nf.format(s.plan_count),
            title: `${s.name} · партий ${nf.format(s.plan_count)}`,
          })),
        ]}
      />

      <Combo
        label="категория"
        width={280}
        value={params.get("cat") ?? ""}
        onChange={(v) => set("cat", v)}
        options={[
          ВСЕ,
          ...categories.map((c) => ({
            value: String(c.id),
            label: c.name,
            meta: nf.format(c.plan_count),
            title: c.name,
          })),
        ]}
      />

      {/* Подпись «плановая», а не «дата»: кнопок с календарём теперь две, и
          одинаковое слово читалось бы как одна. */}
      <DateRangePicker
        label="плановая"
        from={params.get("from") ?? ""}
        to={params.get("to") ?? ""}
        onChange={setRange}
      />

      {/* Дата СОЗДАНИЯ — отдельный отбор, а не замена плановой (замечание
          владельца 16.09.26): у активных партий плановая дата чаще в
          прошлом (225 из 364), и вопросы «что набрали на неделе» и «что
          уезжает до конца месяца» — разные. Складываются через И. */}
      <DateRangePicker
        label="создан"
        from={params.get("cfrom") ?? ""}
        to={params.get("cto") ?? ""}
        windows={созданныеОкна()}
        onChange={setCreatedRange}
      />

      {/* Окно уходимости. Дефолт 60 — тот же, что у карточки товара: два
          разных дефолтных окна в одном кабинете читались бы как одно. Все
          четыре окна приезжают в ответе сразу, поэтому переключение
          пересчитывает колонку БЕЗ похода в базу. */}
      <WindowPicker days={windowDays} onChange={(days) => set("w", String(days))} />

      <Field label="поиск">
        <input
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          placeholder="товар, артикул, поставщик"
          className="h-full w-[190px] bg-transparent px-2.5 text-[13px] outline-none placeholder:text-fg-faint"
        />
      </Field>

      {/* Три переключателя вместо галочки «без QR-папки» (замечание владельца
          23.09.26): «покажи те, что УЖЕ в папке» галочкой не спросить, а
          техкарту и оплату закупщик до сих пор искал в CRM. */}
      <Segmented
        label="QR"
        title="партии в QR-папке либо без неё"
        options={ТРИ}
        isOn={(v) => (params.get("qrhas") ?? "") === v}
        onPick={(v) => {
          // «нет папки» и номер папки вместе не бывают — номер уходит.
          const next = new URLSearchParams(params);
          if (v) next.set("qrhas", v);
          else next.delete("qrhas");
          if (v === "no") next.delete("qr");
          setParams(next, { replace: true });
        }}
        annot="отбор QR-папки"
      />

      {/* Номер папки — вплотную к «QR есть/нет» (разметка 28.09.26): стоя
          отдельно у категории, он не находился там, где его ищут. Номер
          без папки не бывает, поэтому ввод ставит переключатель на «есть». */}
      <Field label="QR №">
        <input
          data-annot="номер QR-папки"
          value={params.get("qr") ?? ""}
          onChange={(e) => {
            const qr = e.target.value.replace(/\D/g, "");
            const next = new URLSearchParams(params);
            if (qr) {
              next.set("qr", qr);
              next.set("qrhas", "yes");
            } else next.delete("qr");
            setParams(next, { replace: true });
          }}
          placeholder="папка"
          className="tnum h-full w-[74px] bg-transparent px-2.5 text-[13px] outline-none placeholder:text-fg-faint"
        />
      </Field>

      <Segmented
        label="карта"
        title="техкарта партии в CRM"
        options={ТРИ}
        isOn={(v) => (params.get("card") ?? "") === v}
        onPick={(v) => set("card", v)}
        annot="отбор техкарты"
      />

      <Segmented
        label="оплата"
        title="оплачена ли QR-папка партии; партии без папки считаются неоплаченными"
        options={ТРИ_ОПЛАТА}
        isOn={(v) => (params.get("paid") ?? "") === v}
        onPick={(v) => set("paid", v)}
        annot="отбор оплаты"
      />

      {/* Сброс ведёт к ДЕФОЛТУ, а не к пустому адресу: кнопка, которая
          видна всегда и ведёт туда же, где ты стоишь, — это шум. */}
      {[...params.keys()].some((key) => key !== "status") ||
      params.get("status") !== "1" ? (
        <button
          type="button"
          onClick={() =>
            setParams(new URLSearchParams({ status: "1" }), { replace: true })
          }
          className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 text-[13px] text-fg-muted hover:text-fg"
        >
          сбросить
        </button>
      ) : null}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg focus-within:border-border-strong">
      <span className="flex h-full items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">
        {label}
      </span>
      {children}
    </div>
  );
}
